/**
 * SkyInputNative - SkyInput with the native input attributes it drops.
 *
 * WHY THIS EXISTS
 * ---------------
 * `SkyInputProps` is a closed interface. Unlike `SkyLinkProps`, which extends
 * `React.AnchorHTMLAttributes`, it declares its own props and nothing else, and
 * the implementation does not spread a rest object onto the underlying element.
 * Measured against the shipped build, NOTHING outside that list reaches the DOM
 * -- not `min`/`max`/`step`, not `pattern`, not `inputMode`, and not even
 * `aria-label` or `data-*`. TypeScript hides this for hyphenated names, which it
 * never excess-property-checks, so `aria-describedby={...}` compiles clean and
 * then silently does nothing.
 *
 * That gap is what kept ~137 native `<input>` elements from migrating: an input
 * needing `onKeyDown`, `min`/`max` or an accessible name had no Sky equivalent.
 *
 * HOW IT CLOSES
 * -------------
 * `SkyInput` DOES forward its ref, and that ref is the real `<input>` element
 * (verified, not assumed). So anything the component drops can be re-applied to
 * the node directly: attributes via `setAttribute`, handlers via
 * `addEventListener`. Callers get the full native surface and keep design-system
 * styling, labelling and validation.
 *
 * TRADE-OFF worth knowing before you reach for it: handlers passed here are
 * NATIVE listeners on the element, not React synthetic events routed through
 * the root. They fire before React's delegated handlers on ancestors, and
 * `stopPropagation` here will not stop a React handler on a parent. For the
 * common cases (`onKeyDown` for Enter-to-submit, `onFocus`, `onPaste`) that
 * distinction does not bite. If it would, keep the native element and say why.
 *
 * Prefer plain `SkyInput` whenever the props it supports are enough. Reach for
 * this only for the gap.
 */

import { SkyInput } from "@skypointcloud/skypoint-ui";
import type { SkyInputProps } from "@skypointcloud/skypoint-ui";
import { useCallback, useEffect, useRef } from "react";
import type { InputHTMLAttributes, Ref } from "react";

/**
 * The props SkyInput handles itself. Anything not on this list is re-applied to
 * the DOM node instead. Pinned to the installed @skypointcloud/skypoint-ui: if
 * the library later widens SkyInputProps, entries here become redundant rather
 * than wrong -- a prop the library handles is simply never re-applied.
 */
const SKY_INPUT_PROP_NAMES = [
  "label",
  "type",
  "placeholder",
  "value",
  "defaultValue",
  "onChange",
  "onBlur",
  "loading",
  "disabled",
  "readOnly",
  "required",
  "hint",
  "validationState",
  "validationMessage",
  "autoValidate",
  "validateOn",
  "customValidator",
  "onValidationChange",
  "contentBefore",
  "contentAfter",
  "prefix",
  "suffix",
  "size",
  "clearable",
  "onClear",
  "showCount",
  "maxLength",
  "name",
  "autoComplete",
  "className",
  "id",
  "testId",
] as const;

const SKY_INPUT_PROPS = new Set<string>(SKY_INPUT_PROP_NAMES);

type NativeOnly = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  (typeof SKY_INPUT_PROP_NAMES)[number]
>;

export type SkyInputNativeProps = SkyInputProps &
  NativeOnly & {
    /** Ref to the underlying input, forwarded from SkyInput. */
    inputRef?: Ref<HTMLInputElement>;
  };

/**
 * React spells DOM attributes in camelCase; the DOM does not. Names that
 * already contain a hyphen (`aria-*`, `data-*`) are used verbatim, everything
 * else lowercases -- which is the correct mapping for every attribute this
 * wrapper is for (`inputMode`, `autoFocus`, `tabIndex`, `minLength`, `pattern`).
 */
const toAttributeName = (key: string) => (key.includes("-") ? key : key.toLowerCase());

const toEventName = (key: string) => key.slice(2).toLowerCase();

const isEventProp = (key: string, value: unknown): value is EventListener =>
  key.startsWith("on") && typeof value === "function";

export const SkyInputNative = ({ inputRef, ...props }: SkyInputNativeProps) => {
  const nodeRef = useRef<HTMLInputElement | null>(null);

  const skyProps: Record<string, unknown> = {};
  const nativeProps: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    (SKY_INPUT_PROPS.has(key) ? skyProps : nativeProps)[key] = value;
  }

  const setRef = useCallback(
    (node: HTMLInputElement | null) => {
      nodeRef.current = node;
      if (typeof inputRef === "function") inputRef(node);
      else if (inputRef) {
        (inputRef as { current: HTMLInputElement | null }).current = node;
      }
    },
    [inputRef],
  );

  // Runs on EVERY render, with a cleanup that undoes the previous pass. Values
  // and handler identities change between renders, and a dependency array over
  // an object rebuilt each render would either never fire or always fire; doing
  // the work unconditionally is both cheaper to reason about and correct.
  useEffect(() => {
    const node = nodeRef.current;
    if (!node) return;

    const listeners: Array<[string, EventListener]> = [];
    const applied: string[] = [];

    for (const [key, value] of Object.entries(nativeProps)) {
      if (isEventProp(key, value)) {
        const event = toEventName(key);
        node.addEventListener(event, value);
        listeners.push([event, value]);
        continue;
      }
      const attr = toAttributeName(key);
      if (value === undefined || value === null) {
        node.removeAttribute(attr);
        continue;
      }
      // ARIA booleans are the string "true"/"false", NOT the HTML boolean-
      // attribute convention of presence-means-true. `aria-invalid=""` is not
      // valid and screen readers ignore it, and `aria-expanded="false"` carries
      // real meaning, so false must be written rather than removed.
      const isAria = attr.startsWith("aria-");
      if (typeof value === "boolean" && !isAria) {
        if (!value) {
          node.removeAttribute(attr);
          continue;
        }
        node.setAttribute(attr, "");
        applied.push(attr);
        continue;
      }
      node.setAttribute(attr, String(value));
      applied.push(attr);
    }

    return () => {
      for (const [event, fn] of listeners) node.removeEventListener(event, fn);
      for (const attr of applied) node.removeAttribute(attr);
    };
  });

  // autoFocus is an attribute the browser only honours at parse time, so
  // setting it above does nothing on an element React created. Focus explicitly.
  const shouldAutoFocus = nativeProps.autoFocus === true;
  useEffect(() => {
    if (shouldAutoFocus) nodeRef.current?.focus();
  }, [shouldAutoFocus]);

  return (
    <SkyInput
      ref={setRef}
      {...(skyProps as SkyInputProps)}
    />
  );
};
